/* eslint-disable no-param-reassign */

import { AttributesToRemoveInfo } from 'src/containers/ProjectDiagram/hooks/remove/types';
import {
  Diagram,
  DiagramLink,
  DiagramLinkLabel,
  DiagramNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { checkAndConfirmCommit } from 'src/containers/ProjectDiagram/utils/checkAndConfirmCommit';

export enum DiagramModelCommitLayout {
  SKIP = 'SKIP',
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
}

type DiagramModelCommitOptions = {
  attributesToRemoveInfo?: AttributesToRemoveInfo[];
  layout?: DiagramModelCommitLayout;
  layoutPartial?: Set<go.Key>;
  shouldCommit?: boolean;
  skipsCheck?: boolean;
  tname?: string;
};

export const commitDiagramModel = async (
  diagram: Diagram,
  commit: (model: Model, diagram: Diagram) => void,
  options: DiagramModelCommitOptions = {}
) => {
  const {
    tname = undefined,
    layout = DiagramModelCommitLayout.SKIP,
    layoutPartial = new Set(),
    shouldCommit = true,
    skipsCheck = false,
  } = options;
  const prevIsOngoing = diagram.layout.isOngoing;

  if (layout === DiagramModelCommitLayout.SKIP) {
    diagram.layout.isOngoing = false;
  } else if (layout === DiagramModelCommitLayout.PARTIAL) {
    diagram.nodes.each((n) => {
      // data can be null for temporary parts
      const node = n as DiagramNode | DiagramLinkLabel | { data: null };
      if (node.data) {
        node.skipsLayoutCommit = !layoutPartial.has(node.data.key);
      }
    });
    diagram.links.each((l) => {
      // data can be null for temporary parts
      const link = l as DiagramLink | { data: null };
      if (link.data) {
        link.skipsLayoutCommit = !layoutPartial.has(link.data.key);
      }
    });
  }

  let didCommit = true;
  if (!shouldCommit) {
    commit(diagram.model, diagram);
  } else if (skipsCheck) {
    // anything that skips check should not be recorded by undomanager,
    // but we still want layout to happen
    diagram.commit((diagram) => commit(diagram.model, diagram), null);
  } else {
    didCommit = await checkAndConfirmCommit(
      diagram,
      commit,
      tname,
      options.attributesToRemoveInfo
    );
  }

  if (
    layout === DiagramModelCommitLayout.FULL ||
    layout === DiagramModelCommitLayout.PARTIAL
  ) {
    diagram.nodes.each((node) => {
      (node as DiagramNode).skipsLayoutCommit = false;
    });
    diagram.links.each((l) => {
      const link = l as DiagramLink;
      link.skipsLayoutCommit = false;
      if (link.isNewlyAdded) {
        // Ensure that go.js always applies the layout to newly-created links
        link.invalidateRoute();
        link.isNewlyAdded = false;
      }
    });
  } else if (layout === DiagramModelCommitLayout.SKIP) {
    diagram.layout.isOngoing = prevIsOngoing;
  }

  return didCommit;
};

/**
 * Makes a commit while having the transaction not recorded by the UndoManager.
 * Use this when layout behavior is not needed (ex: highlighting, deletions)
 */
export const commitDiagramWhileSkippingUndoManager = (
  diagram: Diagram,
  commit: (model: Model, diagram: Diagram) => void,
  tname?: string
) => {
  diagram.skipsUndoManager = true;
  diagram.commit((d) => commit(d.model, d), tname || null);
  diagram.skipsUndoManager = false;
};
